import { useEffect } from "react";
import { Download, FileText, FileSpreadsheet, CheckCircle2, Mail } from "lucide-react";
import SiteHeader from "../components/site/SiteHeader";
import SiteFooter from "../components/site/SiteFooter";
import FloatingDonate from "../components/site/FloatingDonate";
import { useT } from "../i18n/LanguageContext";
import { BUSINESS_PLAN_PDF, BUDGET_2026_XLSX, CONTACT } from "../components/site/siteAssets";

const copy = {
  en: {
    kicker: "Our Plan",
    title: "How we get to a future without FGM.",
    sub: "Our 2025-2030 business plan lays out the strategy: survivor-led advocacy, community workshops, global media campaigns, and policy work at the United Nations.",
    download: "Download the 2025 Business Plan (PDF)",
    downloadSub: "Approved board document · last revised 2025",
    budgetDownload: "Download the 2026 Budget (Excel)",
    budgetSub: "Annual operating + program budget · fiscal year 2026",
    pillarsKicker: "Five pillars",
    pillarsHead: "What your support funds.",
    pillars: [
      { title: "1. Social-media advocacy campaign", body: "Initial funds hire the creative and outreach team that will carry survivor voices across Africa, the diaspora, and global policy spaces." },
      { title: "2. Community workshops in Togo", body: "Survivor-led 4-day workshops in villages — open dialogue, education, and the building of local leadership against FGM." },
      { title: "3. Survivor circles and care", body: "Trauma-informed support groups, referrals, and the documentation of survivor stories with consent." },
      { title: "4. Global media and music", body: "The 2026 advocacy song, films, and media partnerships that move FGM from taboo to public conversation." },
      { title: "5. UN and policy advocacy", body: "Representation at the United Nations and across coalitions to ensure Togolese voices shape global policy." },
    ],
    contactKicker: "Want the full briefing?",
    contactHead: "Talk to the team.",
    contactBody: "Funders, journalists, and partners — email us for the full board deck, latest impact numbers, or to schedule a call with our founder.",
    contactCta: "Email the foundation",
  },
  fr: {
    kicker: "Notre Plan",
    title: "Comment construire un avenir sans MGF.",
    sub: "Notre plan d'affaires 2025-2030 expose la stratégie : un plaidoyer mené par les survivantes, des ateliers communautaires, des campagnes médiatiques mondiales et un travail politique aux Nations Unies.",
    download: "Télécharger le Plan d'Affaires 2025 (PDF)",
    downloadSub: "Document approuvé par le conseil · dernière révision 2025",
    budgetDownload: "Télécharger le Budget 2026 (Excel)",
    budgetSub: "Budget annuel de fonctionnement et de programmes · exercice 2026",
    pillarsKicker: "Cinq piliers",
    pillarsHead: "Ce que votre soutien finance.",
    pillars: [
      { title: "1. Campagne de plaidoyer sur les réseaux sociaux", body: "Les premiers fonds permettront de recruter l'équipe créative et de terrain qui portera la voix des survivantes en Afrique, dans la diaspora et dans les espaces politiques mondiaux." },
      { title: "2. Ateliers communautaires au Togo", body: "Des ateliers de 4 jours menés par des survivantes dans les villages — dialogue ouvert, éducation et leadership local contre les MGF." },
      { title: "3. Cercles de survivantes et accompagnement", body: "Groupes de soutien éclairés par le trauma, orientations et documentation consentie des récits de survivantes." },
      { title: "4. Médias mondiaux et musique", body: "Le morceau de plaidoyer 2026, les films et les partenariats médiatiques qui sortent les MGF du tabou pour les inscrire dans le débat public." },
      { title: "5. Plaidoyer ONU et politique", body: "Représentation aux Nations Unies et au sein des coalitions pour que la voix togolaise façonne les politiques mondiales." },
    ],
    contactKicker: "Vous voulez le dossier complet ?",
    contactHead: "Parlez à l'équipe.",
    contactBody: "Financeurs, journalistes, partenaires — écrivez-nous pour recevoir la présentation complète, les derniers chiffres d'impact, ou planifier un appel avec notre fondatrice.",
    contactCta: "Écrire à la fondation",
  },
};

export default function OurPlan() {
  const t = useT(copy);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#F4EDE0] text-[#2A1F15]" style={{ fontFamily: "'Figtree','Work Sans',sans-serif" }} data-testid="our-plan-page">
      <SiteHeader />

      <section className="py-20 md:py-28 bg-[#2A1F15] text-[#F4EDE0]">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <div className="text-[11px] tracking-[0.35em] uppercase text-[#D4A93B] font-bold">{t.kicker}</div>
          <h1 className="mt-6 text-5xl md:text-7xl leading-[1.05]" style={{ fontFamily: "'Merriweather',serif", fontWeight: 700 }} data-testid="our-plan-title">{t.title}</h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-[#F4EDE0]/85 leading-relaxed" style={{ fontFamily: "'Merriweather',serif" }}>{t.sub}</p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
            <a
              href={BUSINESS_PLAN_PDF}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="our-plan-download"
              className="group inline-flex items-center gap-4 rounded-xl bg-[#F4EDE0] text-[#2A1F15] p-5 md:p-6 hover:bg-[#D4A93B] transition-colors shadow-2xl"
            >
              <div className="h-14 w-14 rounded-lg bg-[#E3282B] text-white flex items-center justify-center shrink-0">
                <FileText size={28} />
              </div>
              <div className="text-left pr-2 flex-1">
                <div className="font-bold text-base md:text-lg leading-snug" style={{ fontFamily: "'Merriweather',serif" }}>{t.download}</div>
                <div className="text-xs md:text-sm text-[#2A1F15]/70 mt-1">{t.downloadSub}</div>
              </div>
              <Download size={22} className="group-hover:translate-y-1 transition-transform" />
            </a>

            <a
              href={BUDGET_2026_XLSX}
              download
              data-testid="our-plan-budget-download"
              className="group inline-flex items-center gap-4 rounded-xl bg-[#F4EDE0] text-[#2A1F15] p-5 md:p-6 hover:bg-[#00A651] hover:text-white transition-colors shadow-2xl"
            >
              <div className="h-14 w-14 rounded-lg bg-[#00A651] text-white flex items-center justify-center shrink-0">
                <FileSpreadsheet size={28} />
              </div>
              <div className="text-left pr-2 flex-1">
                <div className="font-bold text-base md:text-lg leading-snug" style={{ fontFamily: "'Merriweather',serif" }}>{t.budgetDownload}</div>
                <div className="text-xs md:text-sm opacity-70 mt-1">{t.budgetSub}</div>
              </div>
              <Download size={22} className="group-hover:translate-y-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <div className="text-[11px] tracking-[0.35em] uppercase text-[#8C5A3C] font-bold">{t.pillarsKicker}</div>
          <h2 className="mt-4 text-4xl md:text-5xl leading-[1.05]" style={{ fontFamily: "'Merriweather',serif", fontWeight: 700 }}>{t.pillarsHead}</h2>
          <ol className="mt-14 space-y-6">
            {t.pillars.map((p, i) => (
              <li key={i} data-testid={`our-plan-pillar-${i}`} className="flex items-start gap-5 p-6 md:p-7 rounded-xl bg-[#F4EDE0] border-l-4 border-[#E3282B] hover:shadow-xl transition-shadow">
                <CheckCircle2 size={26} className="text-[#00A651] shrink-0 mt-1" />
                <div>
                  <div className="text-xl md:text-2xl text-[#2A1F15]" style={{ fontFamily: "'Merriweather',serif", fontWeight: 700 }}>{p.title}</div>
                  <p className="mt-2 text-base md:text-lg text-[#2A1F15]/80 leading-relaxed" style={{ fontFamily: "'Merriweather',serif" }}>{p.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-[#F4EDE0]">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center">
          <div className="text-[11px] tracking-[0.35em] uppercase text-[#E3282B] font-bold">{t.contactKicker}</div>
          <h2 className="mt-4 text-4xl md:text-5xl leading-[1.05]" style={{ fontFamily: "'Merriweather',serif", fontWeight: 700 }}>{t.contactHead}</h2>
          <p className="mt-6 text-base md:text-lg text-[#2A1F15]/80 leading-relaxed">{t.contactBody}</p>
          <a
            href={`mailto:${CONTACT.email}?subject=${encodeURIComponent("Briefing / Business plan request — Mother Hippo Foundation")}`}
            data-testid="our-plan-email-cta"
            className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#2A1F15] text-[#F4EDE0] font-bold hover:bg-[#E3282B] transition-colors shadow-md"
          >
            <Mail size={16} /> {t.contactCta}
          </a>
        </div>
      </section>

      <SiteFooter />
      <FloatingDonate />
    </div>
  );
}
